import type { InventoryRecipeRecord } from '@/renderer/types/inventory';
import type { MenuItem } from '@/renderer/types/menu';
import { Button } from '@components/common';
import { Pencil, RefreshCw, Trash2 } from 'lucide-react';

const fmtCost = (n: number) =>
  n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

function calcRecipeCost(
  recipe: InventoryRecipeRecord,
  costMap: Map<string, number>,
): number {
  if (!recipe.items?.length) return 0;
  return recipe.items.reduce((sum, item) => {
    const avgCost = costMap.get(item.inventory_item_id) ?? 0;
    return sum + item.quantity * avgCost;
  }, 0);
}

export function RecipesTable({
  recipes,
  menuItems,
  isLoading = false,
  costMap = new Map<string, number>(),
  onEdit,
  onDelete,
}: {
  recipes: InventoryRecipeRecord[];
  menuItems: MenuItem[];
  isLoading?: boolean;
  costMap?: Map<string, number>;
  onEdit: (recipe: InventoryRecipeRecord) => void;
  onDelete: (recipe: InventoryRecipeRecord) => void;
}) {
  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
      <div className="relative h-[34rem] overflow-auto">
        {isLoading ? (
          <div className="pointer-events-none absolute inset-0 z-20 flex items-start justify-center bg-white/65 pt-5">
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-700 shadow-sm">
              <RefreshCw size={13} className="animate-spin" />
              Updating recipes
            </span>
          </div>
        ) : null}
        <table
          className={`w-full text-sm transition-opacity ${isLoading ? 'opacity-60' : ''}`}
          aria-busy={isLoading}
        >
          <thead className="sticky top-0 border-b border-gray-100 bg-gray-50">
            <tr className="text-left">
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Recipe
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Menu item
              </th>
              <th className="px-5 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-500">
                Yield
              </th>
              <th className="px-5 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-500">
                Ingredients
              </th>
              <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                Recipe cost
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Status
              </th>
              <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {recipes.map((recipe) => {
              const menuItem = menuItems.find(
                (m) => m.id === recipe.menu_item_id,
              );
              return (
                <tr key={recipe.id} className="border-t border-gray-100">
                  <td className="px-5 py-4">
                    <div className="font-medium text-gray-900">
                      {recipe.name}
                    </div>
                    {recipe.description ? (
                      <div className="mt-0.5 text-xs text-gray-500">
                        {recipe.description}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-5 py-4 text-gray-600">
                    {menuItem?.name ?? recipe.menu_item_name ?? (
                      <span className="text-gray-400 italic">Unlinked</span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-center text-gray-600">
                    {recipe.yield_quantity}
                  </td>
                  <td className="px-5 py-4 text-center text-gray-600">
                    {recipe.items?.length ?? '—'}
                  </td>
                  <td className="px-5 py-4 text-right font-mono text-sm text-gray-700">
                    {costMap.size > 0 ? (
                      fmtCost(calcRecipeCost(recipe, costMap))
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <span
                      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                        recipe.is_active
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {recipe.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="text"
                        size="sm"
                        onClick={() => onEdit(recipe)}
                      >
                        <Pencil size={14} />
                        Edit
                      </Button>
                      <Button
                        variant="text"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => onDelete(recipe)}
                      >
                        <Trash2 size={14} />
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
